import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { statusColor } from '../../../../utils/helperUtil';
import { ListRegisterRequest } from '../../../../services/soap/features/requests/ListRegisterRequest';
import { ListFormSettingsRequest } from '../../../../services/soap/features/requests/ListFormSettingsRequest';
import { GetFormSettingsRequest } from '../../../../services/soap/features/requests/GetFormSettingsRequest';
import { ListStatusRequest, ListStatusType } from '../../../../services/soap/project/requests/ListStatusRequest';
import { ListActivityAdvancedRequest } from '../../../../services/soap/project/requests/ListActivityAdvancedRequest';
import { GetFormPanelRequest } from '../../../../services/soap/features/requests/GetFormPanelRequest';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { SoapUtils } from '../../../../utils/SoapUtils';
import { SettingItem } from '../../../../services/soap/features/responses/GetFormSettingsResponse';
import { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import { ListCustomViewsRequest } from '../../../../services/soap/features/requests/ListCustomViewsRequest';
import { Order } from '../../../../services/soap/dataObjects/Order';
import {
  ActivityItem,
  ActivityStatus,
  ColumnDefinition,
  FilterColumn,
  ListActivityAdvancedResponse,
  QueryParams,
} from '../types';

interface UseServiceActivitiesDataReturn {
  activities: ActivityItem[];
  isLoading: boolean;
  isViewsLoading: boolean;
  error: Error | null;
  totalCount: number;
  hasMore: boolean;
  loadingState: 'idle' | 'loading' | 'loadingMore';
  activityListKey: string;
  columns: ColumnDefinition[];
  activityStatuses: ActivityStatus[];
  formSettingId: string;
  formSettings: SettingItem[];
  customViews: CustomView[];
  hasMoreCustomViews: boolean;
  loadMoreCustomViews: () => Promise<void>;
  refreshCustomViews: () => Promise<void>;
  fetchActivityLayout: (
    selectedRow: ActivityItem | null,
  ) => Promise<
    | {
        layoutObject: unknown;
        project: unknown;
        activity: unknown;
      }
    | null
  >;
  loadMore: () => Promise<void>;
  showLess: () => void;
  isExpanded: boolean;
  reload: (filters?: QueryParams) => void;
  refreshActivity: (activityId: string | number) => Promise<void>;
}

const PAGE_SIZE = 50;

export function useServiceActivitiesData(selectedApp: string | null): UseServiceActivitiesDataReturn {
  const { store, projectService, featureService } = useDependency();

  const [queryParams, setQueryParams] = useState<QueryParams>({ FILTER: { COL: [] } });
  const [columns, setColumns] = useState<ColumnDefinition[]>([]);
  const [activityStatuses, setActivityStatuses] = useState<ActivityStatus[]>([]);
  const [activityListKey, setActivityListKey] = useState<string>('');
  const [formSettingId, setFormSettingId] = useState<string>('');
  const [formSettings, setFormSettings] = useState<SettingItem[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isViewsLoading, setIsViewsLoading] = useState<boolean>(false);
  const [viewsLoaded, setViewsLoaded] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewsOffset, setCustomViewsOffset] = useState<number>(0);
  const [hasMoreCustomViews, setHasMoreCustomViews] = useState<boolean>(false);
  const initialCountRef = useRef<number>(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isLoadingMoreRef = useRef<boolean>(false);
  const isInitializingRef = useRef<boolean>(false);
  const activitiesRequestIdRef = useRef<number>(0);

  const hasMore = useMemo(() => activities.length < totalCount, [activities.length, totalCount]);

  // Compute loadingState for React Spectrum TableView
  const loadingState = useMemo<'idle' | 'loading' | 'loadingMore'>(() => {
    if (isLoadingMore) {
      return 'loadingMore';
    }
    if (isLoading && activities.length === 0) {
      return 'loading';
    }
    return 'idle';
  }, [isLoading, isLoadingMore, activities.length]);

  const fetchActivityStatuses = useCallback(
    async (listKey: string): Promise<ActivityStatus[]> => {
      if (!listKey) return [];
      const res = await projectService.listActivityStatus2(
        new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Activity, listKey),
      );

      if (res.result === 'OK' && res.STATUS.length > 0) {
        const statusArray: ActivityStatus[] = res.STATUS.map(status => {
          const { id, name, color } = status;
          return { id, name, color: statusColor(String(color)) };
        });
        statusArray.unshift({ id: 'All states', name: 'All states', color: 'black', types: [] });
        return statusArray;
      }
      return [];
    },
    [projectService, store.Server, store.SessionId],
  );

  const fetchActivityListKey = useCallback(async (): Promise<{ activityListKey: string; settingId: string }> => {
    if (!selectedApp) {
      return { activityListKey: '', settingId: '' };
    }
    const resListRegister = await featureService.listRegister(
      new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, Number(selectedApp)),
    );

    const listKey =
      resListRegister?.ROWS?.find((row: { ROW?: { key?: string; value?: string } }) => row.ROW?.key === 'FPATypeCategoryForListActivity')
        ?.ROW?.value ?? '';
    const fetchedSettingId =
      resListRegister?.ROWS?.find((row: { ROW?: { key?: string; value?: string } }) => row.ROW?.key === 'formSetting')?.ROW?.value ?? '';

    return { activityListKey: listKey, settingId: fetchedSettingId };
  }, [featureService, selectedApp, store.Server, store.SessionId]);

  const fetchFormSettings = useCallback(
    async (formSettingId: string): Promise<{ columns: ColumnDefinition[]; settings: SettingItem[] }> => {
      if (!formSettingId) return { columns: [], settings: [] };

      try {
        let actualSettingId: string | number = formSettingId;
        if (actualSettingId === '') {
          const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
          const filteredData = resFormSettings.SETTING.filter(
            (item: { formId?: string; isDefault?: string | number }) => item.formId === 'ListActivityAdvanced' && item.isDefault === '1',
          );
          if (filteredData.length > 0) {
            actualSettingId = filteredData[0].id;
          }
        }

        if (actualSettingId) {
          const response = await featureService.getFormSettings(
            new GetFormSettingsRequest(store.Server, store.SessionId, String(actualSettingId)),
          );
          if (response?.result === 'OK' && response?.count > 0) {
            const allSettings = response.SETTING?.[0]?.ITEM.filter(item => item.visibility == 1) || [];
            const columns = allSettings?.map(item => {
              const key = item.itemname.includes('|') ? item.itemname.split('|')[1] : item.itemname;
              const name = item.caption && item.caption.trim() !== '' ? item.caption : item.name;
              const itemname = item.itemname;
              return { key, name, itemname };
            }) ?? [];

            return { columns, settings: allSettings };
          }
        }
      } catch {
        // fall through to default columns
      }
      return { columns: [{ key: 'created', name: 'Created' }], settings: [] };
    },
    [featureService, store.Server, store.SessionId],
  );

  const ensureFpaFilter = useCallback(
    async (current: QueryParams): Promise<QueryParams> => {
      const hasFpa = current.FILTER.COL.some(col => col.name === 'fpaTypeCategory');
      if (hasFpa && activityListKey) {
        return current;
      }

      const { activityListKey: listKey } = activityListKey ? { activityListKey } : await fetchActivityListKey();

      const nextFilters = [...current.FILTER.COL.filter(col => col.name !== 'fpaTypeCategory')];
      nextFilters.push({ name: 'fpaTypeCategory', type: 'any', value: listKey });

      // Deduplicate by name
      const unique: FilterColumn[] = [];
      const seen = new Set<string>();
      nextFilters.forEach(col => {
        if (!seen.has(col.name)) {
          seen.add(col.name);
          unique.push(col);
        }
      });

      return { ...current, FILTER: { COL: unique } };
    },
    [activityListKey, fetchActivityListKey],
  );

  /**
   * Extracts FIELDS and ORDER from custom view definition if a custom view is selected.
   * 
   * @param customViews - Array of available custom views
   * @param queryParams - Current query parameters to check for selected custom view
   * @returns Object with fields array and order object, or undefined if no custom view is selected
   */
  const extractCustomViewFieldsAndOrder = useCallback(
    (customViews: CustomView[], queryParams: QueryParams): { fields?: string[]; order?: Order } | undefined => {
      // Check if a custom view is selected by looking for "state" filter
      const stateFilter = queryParams.FILTER.COL.find(col => col.name === 'state');
      if (!stateFilter || !stateFilter.value) {
        return undefined;
      }

      const viewId = String(stateFilter.value);
      
      // Find the matching custom view
      const selectedView = customViews.find(view => view.id === viewId);
      if (!selectedView || !selectedView.definition) {
        return undefined;
      }

      try {
        const definition = JSON.parse(selectedView.definition);
        
        // Extract FIELDS from columns[].itemname
        const fields: string[] | undefined = definition.columns
          ?.filter((col: any) => col.isVisible !== false)
          ?.map((col: any) => col.itemname)
          ?.filter((itemname: any) => itemname != null && itemname !== '');

        // Extract ORDER from sorting[]
        const order: Order | undefined = definition.sorting?.length > 0
          ? {
              COL: definition.sorting.map((sort: any) => {
                // If itemname contains '|', split and use the second part
                let sortName = sort.itemname;
                if (sortName && sortName.includes('|')) {
                  const parts = sortName.split('|');
                  if (parts.length > 1 && parts[1]?.trim() !== '') {
                    sortName = parts[1].trim();
                  }
                }
                return {
                  name: sortName,
                  desc: sort.level === 'Descending' ? '1' : '0',
                };
              }),
            }
          : undefined;

        return { fields, order };
      } catch (error) {
        console.error('Error parsing custom view definition:', error);
        return undefined;
      }
    },
    [],
  );

  /**
   * Normalizes "between" filters into backend-supported "greater"/"less" conditions.
   *
   * The UI stores numeric/date range filters as a single entry with:
   *   type: "between"
   *   value: JSON string with either { from, to } (numeric) or { start, end } (date)
   *
   * The backend, however, expects separate conditions (and doesn't understand "between"):
   *   - one with type "greater" for the lower bound
   *   - one with type "less"   for the upper bound
   *
   * This helper expands each such entry into up to two entries, dropping empty bounds.
   * It operates on a cloned structure to avoid mutating the original query params used by the UI.
   */
  const normalizeBetweenFilters = useCallback(
    (params: QueryParams): QueryParams => {
      const sourceCols = params.FILTER?.COL ?? [];
      const normalizedCols: FilterColumn[] = [];

      sourceCols.forEach(col => {
        const type = (col.type as unknown) as string;

        if (type !== 'between') {
          normalizedCols.push(col);
          return;
        }

        const rawValue = col.value;
        if (rawValue == null || rawValue === '') {
          // No usable range information, drop this filter
          return;
        }

        let lower: string | undefined;
        let upper: string | undefined;

        try {
          const parsed = JSON.parse(String(rawValue)) as {
            from?: string | number;
            to?: string | number;
            start?: string | number;
            end?: string | number;
          };
          const fromValue = parsed.from ?? parsed.start;
          const toValue = parsed.to ?? parsed.end;

          if (fromValue !== undefined && fromValue !== null && String(fromValue) !== '') {
            lower = String(fromValue);
          }
          if (toValue !== undefined && toValue !== null && String(toValue) !== '') {
            upper = String(toValue);
          }
        } catch {
          // Malformed JSON, fall back to dropping this filter
          return;
        }

        if (lower) {
          normalizedCols.push({
            ...(col as unknown as any),
            type: 'greater',
            value: lower,
          } as FilterColumn);
        }

        if (upper) {
          normalizedCols.push({
            ...(col as unknown as any),
            type: 'less',
            value: upper,
          } as FilterColumn);
        }
      });

      return {
        ...params,
        FILTER: {
          ...params.FILTER,
          COL: normalizedCols,
        },
      };
    },
    [],
  );

  /**
   * Normalizes "relative_preset" filters into backend-supported "greater"/"less" date conditions.
   *
   * The UI stores relative date presets (e.g., "Today", "Last 7 Days", "This Month") as:
   *   type: "relative_preset"
   *   value: preset ID (e.g., "today", "last_7_days", "this_month")
   *
   * The backend expects date range filters with:
   *   - one with type "greater" for the start date (inclusive)
   *   - one with type "less" for the end date (inclusive, using 23:59:59)
   *
   * This helper converts each relative preset into concrete date ranges based on the current date,
   * formatting dates using SoapUtils.toDateForXml for API compatibility.
   */
  const normalizeRelativePresetFilters = useCallback(
    (params: QueryParams): QueryParams => {
      const sourceCols = params.FILTER?.COL ?? [];
      const normalizedCols: FilterColumn[] = [];

      sourceCols.forEach(col => {
        const type = (col.type as unknown) as string;

        if (type !== 'relative_preset') {
          normalizedCols.push(col);
          return;
        }

        const presetId = String(col.value || '').trim();
        if (!presetId) {
          // No preset ID, drop this filter
          return;
        }

        const now = moment();
        let startDate: Date | undefined;
        let endDate: Date | undefined;

        // Calculate date ranges based on preset ID
        switch (presetId) {
          case 'today': {
            startDate = now.startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'yesterday': {
            const yesterday = now.clone().subtract(1, 'day');
            startDate = yesterday.startOf('day').toDate();
            endDate = yesterday.endOf('day').toDate();
            break;
          }
          case 'tomorrow': {
            const tomorrow = now.clone().add(1, 'day');
            startDate = tomorrow.startOf('day').toDate();
            endDate = tomorrow.endOf('day').toDate();
            break;
          }
          case 'last_7_days': {
            startDate = now.clone().subtract(7, 'days').startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'next_7_days': {
            startDate = now.startOf('day').toDate();
            endDate = now.clone().add(7, 'days').endOf('day').toDate();
            break;
          }
          case 'last_30_days': {
            startDate = now.clone().subtract(30, 'days').startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'next_30_days': {
            startDate = now.startOf('day').toDate();
            endDate = now.clone().add(30, 'days').endOf('day').toDate();
            break;
          }
          case 'last_month': {
            const lastMonth = now.clone().subtract(1, 'month');
            startDate = lastMonth.startOf('month').startOf('day').toDate();
            endDate = lastMonth.endOf('month').endOf('day').toDate();
            break;
          }
          case 'this_month': {
            startDate = now.clone().startOf('month').startOf('day').toDate();
            endDate = now.clone().endOf('month').endOf('day').toDate();
            break;
          }
          case 'next_month': {
            const nextMonth = now.clone().add(1, 'month');
            startDate = nextMonth.startOf('month').startOf('day').toDate();
            endDate = nextMonth.endOf('month').endOf('day').toDate();
            break;
          }
          case 'last_quarter': {
            const lastQuarter = now.clone().subtract(1, 'quarter');
            startDate = lastQuarter.startOf('quarter').startOf('day').toDate();
            endDate = lastQuarter.endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'this_quarter': {
            startDate = now.clone().startOf('quarter').startOf('day').toDate();
            endDate = now.clone().endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'next_quarter': {
            const nextQuarter = now.clone().add(1, 'quarter');
            startDate = nextQuarter.startOf('quarter').startOf('day').toDate();
            endDate = nextQuarter.endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'last_year': {
            const lastYear = now.clone().subtract(1, 'year');
            startDate = lastYear.startOf('year').startOf('day').toDate();
            endDate = lastYear.endOf('year').endOf('day').toDate();
            break;
          }
          case 'this_year': {
            startDate = now.clone().startOf('year').startOf('day').toDate();
            endDate = now.clone().endOf('year').endOf('day').toDate();
            break;
          }
          case 'next_year': {
            const nextYear = now.clone().add(1, 'year');
            startDate = nextYear.startOf('year').startOf('day').toDate();
            endDate = nextYear.endOf('year').endOf('day').toDate();
            break;
          }
          default: {
            // Unknown preset ID, log warning and drop this filter
            console.warn(`Unknown relative preset ID: ${presetId}`);
            return;
          }
        }

        // Format dates and create filter entries
        if (startDate) {
          const formattedStartDate = SoapUtils.toDateForXml(startDate, true);
          if (formattedStartDate) {
            normalizedCols.push({
              ...(col as unknown as any),
              type: 'greater',
              value: formattedStartDate,
            } as FilterColumn);
          }
        }

        if (endDate) {
          // For end date, we need to ensure it's inclusive by using 23:59:59
          // SoapUtils.toDateForXml with onlyDate=true gives 00:00:00, so we need to manually set 23:59:59
          const endDateMoment = moment(endDate);
          const formattedEndDate = endDateMoment.format('YYYY-MM-DD') + 'T23:59:59';
          normalizedCols.push({
            ...(col as unknown as any),
            type: 'less',
            value: formattedEndDate,
          } as FilterColumn);
        }
      });

      return {
        ...params,
        FILTER: {
          ...params.FILTER,
          COL: normalizedCols,
        },
      };
    },
    [],
  );

  const fetchActivityCount = useCallback(
    async (params: QueryParams): Promise<number> => {
      const filtersWithFpa = await ensureFpaFilter(params);
      const normalizedFilters = normalizeRelativePresetFilters(normalizeBetweenFilters(filtersWithFpa));
      
      // Use FIELDS from queryParams if available, otherwise extract from custom view definition, otherwise fall back to columns
      let fields: string[];
      if (params.FIELDS?.field && params.FIELDS.field.length > 0) {
        fields = params.FIELDS.field;
      } else {
        const customViewData = extractCustomViewFieldsAndOrder(customViews, params);
        fields = customViewData?.fields && customViewData.fields.length > 0
          ? customViewData.fields
          : columns.length > 0
          ? columns.map(column => column.key)
          : ['created'];
      }
      
      // Extract ORDER for field extraction
      let order: Order | undefined;
      if (params.ORDER && params.ORDER.COL && params.ORDER.COL.length > 0) {
        order = {
          COL: params.ORDER.COL.map(col => ({
            name: col.name,
            desc: typeof col.desc === 'string' ? col.desc : col.desc ? 'true' : 'false',
          })),
        };
      } else {
        const customViewData = extractCustomViewFieldsAndOrder(customViews, params);
        if (customViewData?.order) {
          order = customViewData.order;
        }
      }
      
      // Extract field names from FILTER.COL and ORDER.COL, then combine with existing fields and ensure uniqueness
      const filterFields = normalizedFilters.FILTER?.COL?.map(col => col.name) || [];
      const orderFields = order?.COL?.map(col => col.name) || [];
      const allFields = [...fields, ...filterFields, ...orderFields];
      // Split fields containing '|' into separate fields
      const splitFields = allFields.flatMap(f => {
        if (f && f.includes('|')) {
          return f.split('|').map(part => part.trim()).filter(part => part !== '');
        }
        return f && f !== '' ? [f] : [];
      });
      const uniqueFields = Array.from(new Set(splitFields));
      
      // Filter out 'state' filter - it's only used internally to track custom view selection, not for API filtering
      const apiFilters = {
        ...normalizedFilters.FILTER,
        COL: normalizedFilters.FILTER?.COL?.filter(col => col.name !== 'state') || [],
      };
      
      const activityRes = await projectService.listActivityAdvanced(
        new ListActivityAdvancedRequest(store.Server, store.SessionId, {
          countOnly: '1',
          FILTER: apiFilters,
          FIELDS: {
            field: uniqueFields,
          },
        }),
      );
      if (activityRes?.result === 'OK') {
        return activityRes.count || 0;
      }
      return 0;
    },
    [columns, customViews, ensureFpaFilter, extractCustomViewFieldsAndOrder, normalizeBetweenFilters, normalizeRelativePresetFilters, projectService, store.Server, store.SessionId],
  );

  const fetchActivities = useCallback(
    async (params: QueryParams, currentOffset: number): Promise<ListActivityAdvancedResponse> => {
      const filtersWithFpa = await ensureFpaFilter(params);
      const normalizedFilters = normalizeRelativePresetFilters(normalizeBetweenFilters(filtersWithFpa));
      
      // Use FIELDS from queryParams if available, otherwise extract from custom view definition, otherwise fall back to columns
      let fields: string[];
      if (params.FIELDS?.field && params.FIELDS.field.length > 0) {
        fields = params.FIELDS.field;
      } else {
        const customViewData = extractCustomViewFieldsAndOrder(customViews, params);
        fields = customViewData?.fields && customViewData.fields.length > 0
          ? customViewData.fields
          : columns.length > 0
          ? columns.map(column => column.key)
          : ['created'];
      }
      
      // Use ORDER from queryParams if available, otherwise extract from custom view definition, otherwise use default
      let order: Order;
      if (params.ORDER && params.ORDER.COL && params.ORDER.COL.length > 0) {
        // Convert QueryParams.ORDER to Order type (ensure desc is string)
        order = {
          COL: params.ORDER.COL.map(col => ({
            name: col.name,
            desc: typeof col.desc === 'string' ? col.desc : col.desc ? 'true' : 'false',
          })),
        };
      } else {
        const customViewData = extractCustomViewFieldsAndOrder(customViews, params);
        order = customViewData?.order || {
          COL: [
            {
              name: 'created',
              desc: 'true',
            },
          ],
        };
      }
      
      // Extract field names from FILTER.COL and ORDER.COL, then combine with existing fields and ensure uniqueness
      const filterFields = normalizedFilters.FILTER?.COL?.map(col => col.name) || [];
      const orderFields = order?.COL?.map(col => col.name) || [];
      const allFields = [...fields, ...filterFields, ...orderFields];
      // Split fields containing '|' into separate fields
      const splitFields = allFields.flatMap(f => {
        if (f && f.includes('|')) {
          return f.split('|').map(part => part.trim()).filter(part => part !== '');
        }
        return f && f !== '' ? [f] : [];
      });
      const uniqueFields = Array.from(new Set(splitFields));
      
      // Filter out 'state' filter - it's only used internally to track custom view selection, not for API filtering
      const apiFilters = {
        ...normalizedFilters.FILTER,
        COL: normalizedFilters.FILTER?.COL?.filter(col => col.name !== 'state') || [],
      };
      
      const response = await projectService.listActivityAdvanced(
        new ListActivityAdvancedRequest(store.Server, store.SessionId, {
          limit: PAGE_SIZE,
          offset: currentOffset,
          FILTER: apiFilters,
          ORDER: order,
          FIELDS: {
            field: uniqueFields,
          },
        }),
      );
      return response as unknown as ListActivityAdvancedResponse;
    },
    [columns, customViews, ensureFpaFilter, extractCustomViewFieldsAndOrder, normalizeBetweenFilters, normalizeRelativePresetFilters, projectService, store.Server, store.SessionId],
  );

  const fetchCustomViews = useCallback(
    async (offset: number = 0, append: boolean = false) => {
      if (!selectedApp) return;

      try {
        // Determine if current user is admin from session (BrowserStorage-backed)
        // Session may store adminTree as boolean or string; handle both
        const adminTreeValue = store.session?.sessionInfo?.adminTree as unknown;
        const isAdmin =
          adminTreeValue === true ||
          adminTreeValue === 'true';

        const response = await featureService.listCustomViews(
          new ListCustomViewsRequest(store.Server, store.SessionId, {
            limit: 100,
            offset: offset,
            // Admin users: fetch all (currentUserOnly = false). Others: only current user's views.
            currentUserOnly: !isAdmin,
            FILTER: {
              COL: [
                {
                  name: 'applicationId',
                  type: 'equals',
                  value: selectedApp,
                },
              ],
            },
          }),
        );

        if (response?.result === 'OK' && response?.CUSTOM_VIEW) {
          const views = Array.isArray(response.CUSTOM_VIEW) ? response.CUSTOM_VIEW : [response.CUSTOM_VIEW];

          if (append) {
            setCustomViews((prev) => [...prev, ...views]);
          } else {
            setCustomViews(views);
          }

          setHasMoreCustomViews(response.count > offset + views.length);
          setCustomViewsOffset(offset + views.length);
        } else {
          if (!append) {
            setCustomViews([]);
          }
          setHasMoreCustomViews(false);
        }
      } catch (error) {
        console.error('Error fetching custom views:', error);
        if (!append) {
          setCustomViews([]);
        }
      }
    },
    [featureService, store.Server, store.SessionId, selectedApp],
  );

  const resetActivitiesState = useCallback(() => {
    setActivities([]);
    setTotalCount(0);
    setOffset(0);
    initialCountRef.current = 0;
    setIsExpanded(false);
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
  }, []);

  const fetchActivityLayout = useCallback(
    async (selectedRow: ActivityItem | null) => {
      if (!selectedRow?.id) {
        return null;
      }

      const { ACTIVITY } = await projectService.getActivity(
        new GetActivityRequest(store.Server, store.SessionId, Number(selectedRow.id)),
      );
      const { PROJECT } = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, Number(ACTIVITY.project)));
      const typeId = selectedRow.type;
      const layout = await featureService.getFormPanel(new GetFormPanelRequest(store.Server, store.SessionId, String(typeId)));

      if (layout.count > 0) {
        const scheme = SoapUtils.parseXmlString2(layout.ROW.SCHEME);
        const layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
        return { layoutObject, project: PROJECT, activity: ACTIVITY };
      }
      return null;
    },
    [featureService, projectService, store.Server, store.SessionId],
  );

  const initializeViews = useCallback(async () => {
    if (!selectedApp) return;
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;
    setIsViewsLoading(true);
    setViewsLoaded(false);
    setError(null);

    // Reset everything that depends on the app
    resetActivitiesState();
    setCustomViews([]);
    setHasMoreCustomViews(false);
    setCustomViewsOffset(0);

    try {
      const { activityListKey: listKey, settingId: fetchedSettingId } = await fetchActivityListKey();
      setActivityListKey(listKey);
      setFormSettingId(fetchedSettingId);

      const [statusList, formSettingsResult] = await Promise.all([
        fetchActivityStatuses(listKey),
        fetchFormSettings(fetchedSettingId),
      ]);
      setActivityStatuses(statusList);
      setColumns(formSettingsResult.columns);
      setFormSettings(formSettingsResult.settings);

      await fetchCustomViews(0, false);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsViewsLoading(false);
      setViewsLoaded(true);
      isInitializingRef.current = false;
    }
  }, [
    fetchActivityListKey,
    fetchActivityStatuses,
    fetchFormSettings,
    fetchCustomViews,
    resetActivitiesState,
    selectedApp,
  ]);

  const loadMore = useCallback(async () => {
    // Prevent concurrent loads and ensure we have more data to load
    if (isLoadingMoreRef.current || isLoading || isLoadingMore || !hasMore) {
      return;
    }
    
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    setError(null);
    
    try {
      const response = await fetchActivities(queryParams, offset);
      const rawActivities = response?.ACTIVITIES?.ACTIVITY;
      const listActivity = Array.isArray(rawActivities) ? rawActivities : rawActivities ? [rawActivities] : [];
      if (listActivity.length > 0) {
        setActivities(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const merged = [...prev, ...listActivity.filter(item => !existingIds.has(item.id))];
          return merged;
        });
        setOffset(prev => prev + listActivity.length);
        setIsExpanded(true);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [fetchActivities, hasMore, isLoading, isLoadingMore, offset, queryParams]);

  const showLess = useCallback(() => {
    if (initialCountRef.current >= activities.length) {
      return; // Already at or below initial count
    }
    setActivities(prev => prev.slice(0, initialCountRef.current));
    setOffset(initialCountRef.current);
    setIsExpanded(false);
  }, [activities.length]);

  const reload = useCallback(
    (filters?: QueryParams) => {
      const nextFilters = filters ?? { FILTER: { COL: [] } };
      resetActivitiesState();
      setQueryParams(nextFilters);
    },
    [resetActivitiesState],
  );

  const loadMoreCustomViews = useCallback(async () => {
    if (!hasMoreCustomViews) return;
    await fetchCustomViews(customViewsOffset, true);
  }, [fetchCustomViews, customViewsOffset, hasMoreCustomViews]);

  const refreshCustomViews = useCallback(async () => {
    await fetchCustomViews(0, false);
  }, [fetchCustomViews]);

  const refreshActivity = useCallback(
    async (_activityId: string | number) => {
      // Keep behavior minimal: callers can trigger a reload via current filters.
      // Layout refresh remains available via fetchActivityLayout when selection changes.
      await fetchActivityLayout(null);
    },
    [fetchActivityLayout],
  );

  // Phase 1: views/setup must be loaded first (on app change only)
  useEffect(() => {
    if (!selectedApp) return;
    void initializeViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp]);

  // Phase 2: activities fetch is reactive to active custom view selection
  useEffect(() => {
    if (!selectedApp) return;
    if (!viewsLoaded) return;

    const stateFilter = queryParams.FILTER?.COL?.find(col => col.name === 'state');
    const activeViewId = stateFilter?.value ? String(stateFilter.value).trim() : '';
    const isValidView = !!activeViewId && customViews.some(v => String(v.id ?? '') === activeViewId);

    // No active view (or invalid view) => do not fetch activities
    if (!isValidView) {
      resetActivitiesState();
      setIsLoading(false);
      return;
    }

    const requestId = ++activitiesRequestIdRef.current;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const count = await fetchActivityCount(queryParams);
        if (activitiesRequestIdRef.current !== requestId) return;
        setTotalCount(count);

        const activitiesResponse = await fetchActivities(queryParams, 0);
        if (activitiesRequestIdRef.current !== requestId) return;

        const rawActivities = activitiesResponse?.ACTIVITIES?.ACTIVITY;
        const listActivity = Array.isArray(rawActivities) ? rawActivities : rawActivities ? [rawActivities] : [];
        setActivities(listActivity);
        setOffset(listActivity.length);
        initialCountRef.current = listActivity.length;
        setIsExpanded(false);
      } catch (err) {
        if (activitiesRequestIdRef.current === requestId) {
          setError(err as Error);
        }
      } finally {
        if (activitiesRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    })();
  }, [
    customViews,
    fetchActivities,
    fetchActivityCount,
    queryParams,
    resetActivitiesState,
    selectedApp,
    viewsLoaded,
  ]);

  const errorValue = error;

  return {
    activities,
    isLoading,
    isViewsLoading,
    error: errorValue,
    totalCount,
    hasMore,
    loadingState,
    activityListKey,
    columns,
    activityStatuses,
    formSettingId,
    formSettings,
    customViews,
    hasMoreCustomViews,
    loadMoreCustomViews,
    refreshCustomViews,
    fetchActivityLayout,
    loadMore,
    showLess,
    isExpanded,
    reload,
    refreshActivity,
  };
}